/**
 * The swimming model, built by reading a decoded FIT file. This is a *view* over the record
 * stream: each Length and Stint points back at its underlying record so edits and recompute
 * can write through. Membership is taken from the file's own `first_length_index` boundaries
 * (laps are grouped after the lengths in the stream, not interleaved).
 */
import { FitError } from '../codec/errors'
import type { DataRecord, FitFile } from '../codec/types'
import {
  GLOBAL,
  LAP,
  LEN,
  LENGTH_TYPE,
  SES,
  SPORT_SWIMMING,
  SUB_SPORT_LAP_SWIMMING,
  getNumber,
} from './fields'

export interface Length {
  /** Ordinal among all lengths (active + idle), 0-based. */
  readonly ordinal: number
  readonly record: DataRecord
  readonly active: boolean
  readonly durationMs: number
  readonly startTime: number
  /** Active-only; 0 for idle lengths. */
  readonly strokes: number
  /** avg_speed in milli-m/s; 0 for idle. */
  readonly avgSpeed: number
  /** swim_stroke enum, or 255 ("not recorded"). */
  readonly swimStroke: number
}

export interface Stint {
  readonly lapRecord: DataRecord
  /** All member lengths in order (active and, for a rest stint, idle). */
  readonly lengths: readonly Length[]
  readonly isRest: boolean
}

export interface Activity {
  readonly file: FitFile
  readonly session: DataRecord
  readonly poolLengthCm: number
  readonly lengths: readonly Length[]
  readonly stints: readonly Stint[]
}

function req(value: number | undefined, what: string): number {
  if (value === undefined) throw new FitError(`missing required field: ${what}`)
  return value
}

export function buildActivity(file: FitFile): Activity {
  const lengthRecords: DataRecord[] = []
  const lapRecords: DataRecord[] = []
  let session: DataRecord | undefined

  for (const record of file.records) {
    if (record.kind !== 'data') continue
    const g = record.def.globalMessageNumber
    if (g === GLOBAL.length) lengthRecords.push(record)
    else if (g === GLOBAL.lap) lapRecords.push(record)
    else if (g === GLOBAL.session && !session) session = record
  }

  if (!session) throw new FitError('This file has no activity session and cannot be opened.')

  const sport = getNumber(session, SES.sport)
  const subSport = getNumber(session, SES.subSport)
  if (sport !== SPORT_SWIMMING || subSport !== SUB_SPORT_LAP_SWIMMING) {
    throw new FitError(
      'This is not an indoor/pool (lap) swim. Swim Editor only edits pool-swim activities, ' +
        'which record individual lengths.',
    )
  }
  if (lengthRecords.length === 0) {
    throw new FitError('This pool swim has no recorded lengths to edit.')
  }

  const lengths: Length[] = lengthRecords.map((record, ordinal) => {
    const active = getNumber(record, LEN.lengthType) === LENGTH_TYPE.active
    return {
      ordinal,
      record,
      active,
      durationMs: req(getNumber(record, LEN.totalElapsed), 'length.total_elapsed_time'),
      startTime: req(getNumber(record, LEN.startTime), 'length.start_time'),
      strokes: active ? (getNumber(record, LEN.totalStrokes) ?? 0) : 0,
      avgSpeed: active ? (getNumber(record, LEN.avgSpeed) ?? 0) : 0,
      swimStroke: getNumber(record, LEN.swimStroke) ?? 255,
    }
  })

  const stints = buildStints(lapRecords, lengths)

  return {
    file,
    session,
    poolLengthCm: req(getNumber(session, 44), 'session.pool_length'),
    lengths,
    stints,
  }
}

/**
 * Slice the length list into stints at each lap's `first_length_index`. Laps are sorted by
 * that index; every length must fall into exactly one stint (asserted).
 */
function buildStints(lapRecords: DataRecord[], lengths: Length[]): Stint[] {
  const laps = lapRecords
    .map((lapRecord) => ({
      lapRecord,
      firstLengthIndex: req(getNumber(lapRecord, LAP.firstLengthIndex), 'lap.first_length_index'),
    }))
    .sort((a, b) => a.firstLengthIndex - b.firstLengthIndex)

  if (laps.length === 0 || laps[0]!.firstLengthIndex !== 0) {
    throw new FitError('laps do not start at length index 0')
  }

  const stints: Stint[] = []
  for (let i = 0; i < laps.length; i++) {
    const start = laps[i]!.firstLengthIndex
    const end = i + 1 < laps.length ? laps[i + 1]!.firstLengthIndex : lengths.length
    if (end < start || end > lengths.length) {
      throw new FitError(`lap ${i} has an out-of-range length range [${start}, ${end})`)
    }
    const members = lengths.slice(start, end)
    stints.push({
      lapRecord: laps[i]!.lapRecord,
      lengths: members,
      isRest: members.every((l) => !l.active),
    })
  }

  const covered = stints.reduce((n, s) => n + s.lengths.length, 0)
  if (covered !== lengths.length) {
    throw new FitError(`stints cover ${covered} lengths but there are ${lengths.length}`)
  }
  return stints
}
